import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Upload, FileText, AlertCircle, Users, CheckCircle2,
  Clock, LogOut, RefreshCw, Search, Filter, ChevronLeft, ChevronRight,
  ExternalLink, Plus, Eye, AlertTriangle, Loader2, Download, CalendarIcon, Mic
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AssessmentUpload from './AssessmentUpload';
import IssueReport from './IssueReport';
import VoiceRecordingsTab from './VoiceRecordingsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const regionBadges = {
  usa: { label: 'USA', color: 'bg-blue-100 text-blue-800' },
  canada: { label: 'Canada', color: 'bg-red-100 text-red-800' },
  jamaica: { label: 'Jamaica', color: 'bg-green-100 text-green-800' },
  barbados: { label: 'Barbados', color: 'bg-amber-100 text-amber-800' },
  trinidad_tobago: { label: 'T&T', color: 'bg-purple-100 text-purple-800' },
  panama: { label: 'Panama', color: 'bg-red-100 text-red-800' }
};

const statusColors = {
  pending: 'bg-gray-100 text-gray-800',
  invited: 'bg-blue-100 text-blue-800',
  started: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  submitted: 'bg-emerald-100 text-emerald-800',
};

const useAuth = (navigate) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userInfo = localStorage.getItem('userInfo');
    if (!token || !userInfo) {
      toast.error('Please login to access this dashboard');
      navigate('/login');
      return;
    }
    try {
      const user = JSON.parse(userInfo);
      if (user.role !== 'super_support' && user.role !== 'admin') {
        toast.error('Access denied. Super Support role required.');
        navigate('/login');
        return;
      }
      setIsAuthorized(true);
    } catch {
      navigate('/login');
    } finally {
      setIsChecking(false);
    }
  }, [navigate]);

  return { isAuthorized, isChecking };
};

export default function SuperSupportDashboard() {
  const navigate = useNavigate();
  const { isAuthorized, isChecking } = useAuth(navigate);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const token = localStorage.getItem('token');
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/super-support/dashboard-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  }, [token]);

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '20' });
      if (searchQuery) params.append('search', searchQuery);
      if (regionFilter !== 'all') params.append('region', regionFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('assessment_type', typeFilter);

      const res = await fetch(`${API_URL}/api/super-support/assessments?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAssessments(data.assessments);
        setTotalPages(data.totalPages);
        setTotal(data.total);
      }
    } catch (err) {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  }, [token, page, searchQuery, regionFilter, statusFilter, typeFilter]);

  useEffect(() => {
    if (isAuthorized) {
      fetchStats();
      fetchAssessments();
    }
  }, [fetchStats, fetchAssessments, isAuthorized]);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-cyan-600" />
      </div>
    );
  }
  if (!isAuthorized) return null;

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'assessments', label: 'All Assessments', icon: FileText },
    { id: 'voice', label: 'Voice Recordings', icon: Mic },
    { id: 'upload', label: 'Upload Assessment', icon: Upload },
    { id: 'issues', label: 'Issue Report', icon: AlertCircle },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="super-support-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-cyan-600 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Super Support</h1>
              <p className="text-xs text-slate-500">{userInfo.fullName || userInfo.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-cyan-600 text-cyan-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-[1400px] mx-auto px-6 py-6">
        {activeTab === 'overview' && (
          <OverviewTab stats={stats} onRefresh={fetchStats} />
        )}
        {activeTab === 'assessments' && (
          <AssessmentsTab
            assessments={assessments}
            loading={loading}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            regionFilter={regionFilter}
            setRegionFilter={setRegionFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            total={total}
            onRefresh={fetchAssessments}
            token={token}
          />
        )}
        {activeTab === 'upload' && (
          <AssessmentUpload token={token} onSuccess={() => { fetchStats(); fetchAssessments(); setActiveTab('assessments'); }} />
        )}
        {activeTab === 'voice' && (
          <VoiceRecordingsTab token={token} />
        )}
        {activeTab === 'issues' && (
          <IssueReport token={token} />
        )}
      </main>
    </div>
  );
}

function OverviewTab({ stats, onRefresh }) {
  if (!stats) return <div className="text-center py-12 text-slate-500">Loading stats...</div>;

  const cards = [
    { label: 'Total Assessments', value: stats.totalAssessments, icon: FileText, color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
    { label: 'Self Assessments', value: stats.selfAssessments, icon: Users, color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { label: 'Manager Validations', value: stats.managerValidations, icon: CheckCircle2, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    { label: 'Completed', value: stats.completed, icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { label: 'In Progress', value: stats.inProgress, icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200' },
    { label: 'Pending', value: stats.pending, icon: Clock, color: 'bg-slate-50 text-slate-700 border-slate-200' },
    { label: 'Total Issues', value: stats.totalIssues, icon: AlertCircle, color: 'bg-rose-50 text-rose-700 border-rose-200' },
    { label: 'Open Issues', value: stats.openIssues, icon: AlertTriangle, color: 'bg-orange-50 text-orange-700 border-orange-200' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-slate-900">Dashboard Overview</h2>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-stats-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
        {cards.map((card, i) => (
          <Card key={i} className={`border ${card.color}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <card.icon className="w-5 h-5 opacity-70" />
              </div>
              <div className="text-2xl font-bold">{card.value}</div>
              <div className="text-xs opacity-70 mt-1">{card.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function AssessmentsTab({
  assessments, loading, searchQuery, setSearchQuery,
  regionFilter, setRegionFilter, statusFilter, setStatusFilter,
  typeFilter, setTypeFilter, page, setPage, totalPages, total, onRefresh, token
}) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  const allOnPageSelected = assessments.length > 0 && assessments.every(a => selectedIds.has(a.id));

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      const newSet = new Set(selectedIds);
      assessments.forEach(a => newSet.delete(a.id));
      setSelectedIds(newSet);
    } else {
      const newSet = new Set(selectedIds);
      assessments.forEach(a => newSet.add(a.id));
      setSelectedIds(newSet);
    }
  };

  const toggleSelect = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
  };

  const handleExport = () => {
    setExporting(true);
    const exportBody = {
      selectedIds: selectedIds.size > 0 ? Array.from(selectedIds) : [],
      search: searchQuery,
      region: regionFilter,
      status: statusFilter,
      assessmentType: typeFilter
    };
    if (dateRange.from) exportBody.dateFrom = format(dateRange.from, 'yyyy-MM-dd');
    if (dateRange.to) exportBody.dateTo = format(dateRange.to, 'yyyy-MM-dd');
    const body = JSON.stringify(exportBody);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/super-support/export-assessments`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.responseType = 'blob';

    xhr.onload = function () {
      setExporting(false);
      if (xhr.status < 200 || xhr.status >= 300) {
        toast.error('Export failed. Please try again.');
        return;
      }
      const blob = xhr.response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'assessments_export_' + new Date().toISOString().slice(0,10) + '.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(selectedIds.size > 0
        ? 'Exported ' + selectedIds.size + ' selected assessment(s)'
        : 'Exported all assessments matching filters'
      );
    };

    xhr.onerror = function () {
      setExporting(false);
      toast.error('Export failed. Please check your connection.');
    };

    xhr.send(body);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-slate-900">All Assessments ({total})</h2>
          {selectedIds.size > 0 && (
            <Badge className="bg-cyan-100 text-cyan-700">{selectedIds.size} selected</Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm"
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-csv-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            {selectedIds.size > 0 ? `Export ${selectedIds.size} Selected` : 'Export All'}
          </Button>
          {selectedIds.size > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="clear-selection-btn">
              Clear Selection
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4" data-testid="assessment-filters">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search by name, email, manager..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            data-testid="search-input"
          />
        </div>
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[160px]" data-testid="type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="self_assessment">Self Assessment</SelectItem>
            <SelectItem value="manager_validation">Manager Validation</SelectItem>
            <SelectItem value="technical_assessment">Technical Assessment</SelectItem>
            <SelectItem value="hands_on_simulation">Hands-On Simulation</SelectItem>
          </SelectContent>
        </Select>
        <Select value={regionFilter} onValueChange={(v) => { setRegionFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]" data-testid="region-filter">
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            <SelectItem value="usa">USA</SelectItem>
            <SelectItem value="canada">Canada</SelectItem>
            <SelectItem value="jamaica">Jamaica</SelectItem>
            <SelectItem value="barbados">Barbados</SelectItem>
            <SelectItem value="trinidad_tobago">Trinidad & Tobago</SelectItem>
            <SelectItem value="panama">Panama</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="started">Started</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="text-sm h-10" data-testid="ss-date-filter-btn">
              <CalendarIcon className="w-4 h-4 mr-2" />
              {dateRange.from ? (
                dateRange.to ? (
                  <>{format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d')}</>
                ) : format(dateRange.from, 'MMM d, yyyy')
              ) : 'Filter by Date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={(range) => {
                setDateRange(range || { from: undefined, to: undefined });
                if (range?.to) setDatePopoverOpen(false);
              }}
              numberOfMonths={2}
            />
            {dateRange.from && (
              <div className="px-3 pb-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => { setDateRange({ from: undefined, to: undefined }); setDatePopoverOpen(false); }}>
                  Clear
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="assessments-table">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="p-3 w-10">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 accent-cyan-600 cursor-pointer"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="text-left p-3 font-medium text-slate-600">Name</th>
                <th className="text-left p-3 font-medium text-slate-600">Type</th>
                <th className="text-left p-3 font-medium text-slate-600">Region</th>
                <th className="text-left p-3 font-medium text-slate-600">Status</th>
                <th className="text-left p-3 font-medium text-slate-600">Progress</th>
                <th className="text-left p-3 font-medium text-slate-600">Manager</th>
                <th className="text-left p-3 font-medium text-slate-600">Date Uploaded</th>
                <th className="text-left p-3 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center p-8 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></td></tr>
              ) : assessments.length === 0 ? (
                <tr><td colSpan={9} className="text-center p-8 text-slate-400">No assessments found</td></tr>
              ) : assessments.map((a) => (
                <tr key={a.id} className={`border-b hover:bg-slate-50 ${selectedIds.has(a.id) ? 'bg-cyan-50/50' : ''}`} data-testid={`assessment-row-${a.id}`}>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(a.id)}
                      onChange={() => toggleSelect(a.id)}
                      className="w-4 h-4 accent-cyan-600 cursor-pointer"
                      data-testid={`select-checkbox-${a.id}`}
                    />
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-slate-900">{a.fullName}</div>
                    <div className="text-xs text-slate-400">{a.email}</div>
                  </td>
                  <td className="p-3">
                    <Badge variant="outline" className="text-xs">
                      {(a.assessmentType || 'self_assessment').replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="p-3">
                    {(() => {
                      const r = regionBadges[a.region?.toLowerCase()] || { label: a.region || '-', color: 'bg-gray-100 text-gray-800' };
                      return <Badge className={`${r.color} text-xs`}>{r.label}</Badge>;
                    })()}
                  </td>
                  <td className="p-3">
                    <Badge className={`${statusColors[a.status] || 'bg-gray-100 text-gray-800'} text-xs`}>
                      {(a.status || 'pending').replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${a.progress || 0}%` }} />
                      </div>
                      <span className="text-xs text-slate-500">{a.progress || 0}%</span>
                    </div>
                  </td>
                  <td className="p-3 text-xs text-slate-500">{a.managerName || '-'}</td>
                  <td className="p-3 text-xs text-slate-500" data-testid={`date-uploaded-${a.id}`}>
                    {a.createdAt ? format(new Date(a.createdAt), 'MMM d, yyyy') : '-'}
                  </td>
                  <td className="p-3">
                    <a
                      href={a.assessmentLink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-cyan-600 hover:text-cyan-800 text-xs flex items-center gap-1"
                      data-testid={`view-assessment-${a.id}`}
                    >
                      <Eye className="w-3 h-3" /> View
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t bg-slate-50">
            <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
